<template>
    <div>
        <!-- Minimized Bubble -->
        <div v-if="isMinimized && active" class="lti-bubble">
            <div class="bubble-content px-4 py-6">
                <e-button ref="bubbleIcon" type="is-dark" :aria-label="title">
                    <i class="pi pi-book" aria-hidden="true" />
                </e-button>

                <div class="ml-1 bubble-text">
                    <strong class="bubble-title">
                        {{ title }}
                    </strong>
                </div>

                <div class="bubble-divider" aria-hidden="true" />

                <div class="lti-drawer__button-group bubble-buttons" role="group" aria-label="LTI drawer controls">
                    <e-button
                        ref="bubbleRestore"
                        type="is-dark"
                        icon-left="pi pi-arrow-up-right-and-arrow-down-left-from-center"
                        size="is-small"
                        class="lti-drawer__group-btn"
                        aria-label="Restore drawer"
                        @click="restore"
                    />
                    <e-button
                        type="is-dark"
                        size="is-small"
                        title="Close"
                        class="lti-drawer__group-btn"
                        aria-label="Close drawer"
                        @click="close"
                    >
                        <i class="pi pi-times" aria-hidden="true" />
                    </e-button>
                </div>
            </div>
        </div>

        <!-- Sidebar Drawer -->
        <e-sidebar
            v-bind="$attrs"
            v-model="internalActive"
            fullheight
            right
            :can-cancel="['escape', 'outside']"
            :on-cancel="close"
            class="lti-drawer"
            :class="{ 'is-maximized': isMaximized, 'is-hidden-visually': isMinimized && active }"
            :destroy-on-hide="false"
            :overlay="!isMinimized"
        >
            <aside
                ref="drawerAside"
                class="card has-no-box-shadow is-flex is-flex-vertical is-height-full"
                :aria-hidden="isMinimized && active"
                :tabindex="isMinimized && active ? -1 : null"
                :inert="isMinimized && active"
            >
                <div class="card-header">
                    <div class="card-header-title is-flex is-align-items-center">
                        <edverum-logo v-if="!hideLogo" class="mr-3 edverum-logo-compact" hide-text />

                        <div class="header-text">
                            <span class="header-title">{{ title }}</span>
                        </div>
                    </div>

                    <div class="card-header-icon" aria-label="more options">
                        <div class="lti-drawer__button-group" role="group" aria-label="Drawer window controls">
                            <e-button
                                :icon-left="
                                    isMaximized ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center' : 'pi pi-expand'
                                "
                                type="is-dark"
                                size="is-small"
                                :title="isMaximized ? 'Restore Size' : 'Full Screen'"
                                :aria-label="isMaximized ? 'Restore size' : 'Full screen'"
                                class="lti-drawer__group-btn"
                                @click="toggleMaximize"
                            />
                            <e-button
                                type="is-dark"
                                size="is-small"
                                title="Minimize"
                                class="lti-drawer__group-btn"
                                aria-label="Minimize drawer"
                                @click="minimize"
                            >
                                <i class="pi pi-minus" aria-hidden="true" />
                            </e-button>

                            <e-button
                                type="is-dark"
                                size="is-small"
                                title="Close"
                                class="lti-drawer__group-btn"
                                aria-label="Close drawer"
                                @click="close"
                            >
                                <i class="pi pi-times" aria-hidden="true" />
                            </e-button>
                        </div>
                    </div>
                </div>

                <div class="card-content p-5 is-flex-grow-1 is-scrollable">
                    <slot />
                </div>

                <footer v-if="$slots.footer" class="lti-drawer__footer">
                    <slot name="footer" />
                </footer>
            </aside>
        </e-sidebar>
    </div>
</template>

<script>
import EdverumLogo from '@/components/elements/EdverumLogo.vue';

export default {
    name: 'LtiDrawer',

    components: {
        EdverumLogo,
    },

    props: {
        active: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'LTI Tool',
        },
        refreshOnClose: {
            type: Boolean,
            default: false,
        },
        isEbook: {
            type: Boolean,
            default: false,
        },
        hideLogo: {
            type: Boolean,
            default: false,
        },
        maximized: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:active', 'close', 'restore', 'state'],

    data() {
        return {
            // track whether the drawer was minimized so restores from the
            // bubble can be distinguished from other restores
            // store previously focused element so we can restore focus
            isMaximized: this.maximized,
            isMinimized: false,
            previouslyFocused: null,
        };
    },

    computed: {
        internalActive: {
            get() {
                return this.active;
            },

            set(value) {
                if (!value) {
                    this.close();
                }
            },
        },
        // Only prefix when explicitly marked as an ebook
        shouldPrefix() {
            return !!this.isEbook;
        },
    },

    watch: {
        maximized(newVal) {
            this.isMaximized = newVal;
        },
        active(newVal) {
            if (!newVal) {
                this.resetState();
                return;
            }

            // When drawer becomes active, sync maximized state with prop
            // (handles the case where `maximized` is a constant true prop)
            this.isMaximized = this.maximized;
        },
    },

    methods: {
        resetState() {
            this.isMinimized = false;
            this.isMaximized = false;
        },

        close() {
            this.$emit('update:active', false);
            this.$emit('close');
            this.resetState();

            if (this.refreshOnClose) {
                window.location.reload();
            }
        },

        minimize() {
            // Save current focus so we can restore later
            try {
                this.previouslyFocused = document.activeElement;
            } catch (e) {
                console.error('[LtiDrawer:minimize] Failed to capture previously focused element', {
                    operation: 'capture-previously-focused',
                    error: e,
                });
                this.previouslyFocused = null;
                this.$edverumToast?.open({
                    message: 'Unable to save focus state',
                    type: 'is-warning',
                    duration: 3000,
                    position: 'is-top',
                });
            }

            this.isMinimized = true;

            // Move focus to the visible bubble restore control
            this.$nextTick(() => {
                try {
                    const btn = this.$refs.bubbleRestore;
                    const el = btn?.$el ?? btn;
                    el?.focus?.();
                } catch (e) {
                    console.error('[LtiDrawer:minimize] Failed to move focus to bubble restore control', {
                        operation: 'focus-bubble-restore',
                        ref: 'bubbleRestore',
                        error: e,
                    });
                    this.$edverumToast?.open({
                        message: 'Unable to move focus to restore button',
                        type: 'is-warning',
                        duration: 3000,
                        position: 'is-top',
                    });
                }
            });
        },

        restore() {
            const wasMinimized = this.isMinimized;
            this.isMinimized = false;

            this.$nextTick(() => {
                // Always emit with payload for consistent API.
                // Handlers that don't need the payload can safely ignore it.
                const payload = {
                    relaunch: !wasMinimized,
                    wasMinimized,
                };

                this.$emit('restore', payload);

                // Restore keyboard focus to previously focused element (if any).
                try {
                    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
                        this.previouslyFocused.focus();
                    } else {
                        // Fallback: focus first actionable element inside drawer
                        const el =
                            this.$refs.drawerAside && this.$refs.drawerAside.querySelector
                                ? this.$refs.drawerAside.querySelector(
                                      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                                  )
                                : null;
                        el && el.focus && el.focus();
                    }
                } catch {
                    // Ignore focus restoration failures; the drawer remains usable without this fallback.
                }
            });
        },

        toggleMaximize() {
            this.isMaximized = !this.isMaximized;
            this.$emit('state', this.isMaximized);
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.lti-drawer .sidebar-content {
    /* allow override via CSS variable --lti-sidebar-width */
    width: var(--lti-sidebar-width, 75%) !important;

    @include until($navbar-breakpoint) {
        width: 100% !important;
    }
}

/* Apply overlay to the actual sibling/background element */
.lti-drawer .sidebar-background {
    background-color: var(--lti-overlay-bg, rgba(0, 0, 0, 0.5)) !important;
}

.p-drawer-right .lti-drawer.p-drawer,
.lti-drawer.p-drawer.p-drawer-right {
    /* allow override via CSS variable --lti-sidebar-maxwidth */
    width: var(--lti-sidebar-maxwidth, 75vw) !important;
    max-width: var(--lti-sidebar-maxwidth, 75vw) !important;
    box-sizing: border-box;
    overflow-x: hidden;
}

/* Shared transitions for all drawer variants */
.lti-drawer .sidebar-content,
.p-drawer-right .lti-drawer.p-drawer,
.lti-drawer.p-drawer.p-drawer-right {
    transition:
        width 0.3s ease,
        max-width 0.3s ease,
        transform 0.3s ease;
}

/* Maximized state - full width while maintaining right-side position */
.p-drawer-right .lti-drawer.is-maximized.p-drawer,
.lti-drawer.is-maximized.p-drawer.p-drawer-right {
    width: 100vw !important;
    max-width: 100vw !important;
}

/* Minimized state - slide drawer upward and fade out */
.lti-drawer.is-hidden-visually .sidebar-content,
.p-drawer-right .lti-drawer.is-hidden-visually.p-drawer,
.lti-drawer.is-hidden-visually.p-drawer.p-drawer-right {
    transform: translateY(-100%) !important;
}

.is-scrollable {
    overflow-y: auto;
}

.lti-bubble {
    position: fixed;
    top: 70px; /* adjust based on your navbar height */
    left: 50%;
    transform: translateX(-50%) translateY(0);
    z-index: var(--lti-bubble-z, 20);
    transition:
        transform 0.32s ease,
        opacity 0.32s ease;

    .bubble-content {
        min-width: 220px; /* wider like your screenshot */
        height: 48px;
        padding: 0 16px;
        border-radius: 12px;
        background-color: white;
        color: #333;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        border: 1px solid rgba(0, 0, 0, 0.12);
        box-shadow:
            0 -2px 8px rgba(0, 0, 0, 0.08),
            0 4px 12px rgba(0, 0, 0, 0.12);
        cursor: pointer;
    }
    .bubble-divider {
        width: 1px;
        height: 48px;
        background-color: lightgrey;
        margin: 0 12px;
        align-self: center;
    }
}

.app-dark .lti-bubble .bubble-content {
    background-color: black; /* dark background like is-dark */
    color: white;
}

.lti-drawer__button-group {
    display: inline-flex;
    border: 1px solid lightgray;
    border-radius: 6px;
    overflow: hidden;
}

/* Bubble text must be able to shrink so controls remain visible */
.bubble-text {
    flex: 1 1 auto;
    min-width: 0; /* allows child to truncate */
}

.bubble-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Responsive adjustments: keep the bubble within viewport and allow title to wrap */
@media screen and (max-width: 1024px) {
    /* Allow wrapping / multi-line clamp on small and medium viewports so
       very long titles remain readable without being clipped. */
    .lti-bubble .bubble-content {
        min-width: 0;
        max-width: calc(100vw - 2rem);
        width: auto;
        height: auto;
        padding: 8px 12px;
        align-items: flex-start;
        gap: 0.5rem;
        flex-wrap: wrap;
    }

    .lti-bubble {
        left: 50%;
        transform: translateX(-50%);
    }

    /* Clamp title to two lines for mid-size viewports, and allow full wrap
       on very small screens for better readability. */
    .bubble-title {
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        /* Standard property for browsers implementing the newer spec */
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    .bubble-subtitle {
        white-space: normal;
    }
}

@media screen and (max-width: 420px) {
    /* Very small screens: allow the title to take up more vertical space */
    .bubble-title {
        line-clamp: 3;
        -webkit-line-clamp: 3;
    }
}

.bubble-subtitle {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0.8;
    font-size: 0.75rem;
}

.bubble-buttons {
    flex: 0 0 auto; /* never shrink */
}

.bubble-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #1f2937; /* dark background like is-dark */
    color: white;
}

.lti-drawer__button-group .lti-drawer__group-btn {
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
}

.lti-drawer__button-group .lti-drawer__group-btn:not(:last-child) {
    border-right: 1px solid lightgrey !important;
}

/* Mobile: meet 44×44px minimum touch targets for drawer window controls */
@media screen and (max-width: 640px) {
    .lti-drawer .card-header .card-header-icon .lti-drawer__button-group .lti-drawer__group-btn {
        min-width: 44px !important;
        min-height: 44px !important;
        width: 44px !important;
        height: 44px !important;
        padding: 0 !important;
    }
}

.lti-drawer__footer {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0.85rem 1.25rem;
    border-top: 1px solid #e5e7eb;
    background: #fff;
}

/* Dark mode override for drawer footer: replace light background and border */
.app-dark .lti-drawer__footer {
    border-top-color: rgba(255, 255, 255, 0.06);
    background: #0f1720; /* near Tailwind gray-900 */
    color: #f8fafc; /* near Tailwind gray-50 for readable text */
}
</style>
<style>
/* Remove faint dividing line that can appear while scrolling inside the drawer.
   This targets the drawer's card and inner content to avoid the 1px separator
   caused by borders/box-shadows on nested elements. It's intentionally scoped
   to `.lti-drawer` so it won't affect other cards. */
.lti-drawer .card {
    border: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
}

.lti-drawer .card-content {
    background-color: transparent !important;
}

.lti-drawer.p-drawer {
    box-shadow: none !important;
}

/* Keep drawer chrome inside the panel — partial-width drawers on mobile must not clip controls */
.lti-drawer aside.card {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
}

.lti-drawer .card-header {
    min-width: 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    box-sizing: border-box;
    gap: 0.25rem;
}

.lti-drawer .card-header .card-header-title {
    min-width: 0;
    flex: 1 1 0;
    overflow: hidden;
}

.lti-drawer .card-header .card-header-icon {
    flex: 0 0 auto;
    flex-shrink: 0;
    padding-left: 0.25rem;
    padding-right: 0.5rem;
}

/* Header title area: allow truncation so restore/close buttons remain visible */
.lti-drawer .card-header .card-header-title .header-text {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden;
}

.lti-drawer .card-header .card-header-title .header-title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.lti-drawer .card-header .card-header-icon .lti-drawer__button-group {
    flex: 0 0 auto; /* keep buttons visible */
}

/* Nudge the drawer's header up so it visually aligns with the background header */
.lti-drawer .card-header {
    transform: translateY(var(--lti-drawer-header-offset, -0.75rem));
}

.lti-drawer .card-content {
    transform: translateY(var(--lti-drawer-content-offset, -1.375rem));
}

/* Responsive offset adjustments */
@media screen and (max-width: 768px) {
    .lti-drawer {
        --lti-drawer-header-offset: -0.5rem;
        --lti-drawer-content-offset: -0.75rem;
    }
}

@media screen and (min-width: 769px) and (max-width: 1023px) {
    .lti-drawer {
        --lti-drawer-header-offset: -0.65rem;
        --lti-drawer-content-offset: -0.9rem;
    }
}

@media screen and (min-width: 1024px) {
    .lti-drawer {
        --lti-drawer-header-offset: -0.75rem;
        --lti-drawer-content-offset: -1.375rem;
    }
}

/* Responsive drawer sizing and header title constraints
   - Adjust CSS variables to control the drawer width at different
     breakpoints so the drawer is narrower on wide screens and
     full-width on small devices.
   - Constrain the header title's maximum width using calc() so the
     logo and control buttons always remain visible without overlap.
*/
.lti-drawer {
    --lti-sidebar-width: 75%;
    --lti-sidebar-maxwidth: 75vw;
}

@media screen and (max-width: 1200px) {
    .lti-drawer {
        --lti-sidebar-width: 85%;
        --lti-sidebar-maxwidth: 85vw;
    }
}

@media screen and (max-width: 640px) {
    .lti-drawer {
        --lti-sidebar-width: 100%;
        --lti-sidebar-maxwidth: 100vw;
    }

    .lti-drawer .card-header .card-header-title .header-title {
        font-size: 1rem;
    }

    .lti-drawer .card-header .card-header-title {
        padding-right: 0.25rem;
    }
}

@media screen and (max-width: 480px) {
    .lti-drawer .card-header .card-header-title .header-title {
        font-size: 0.95rem;
    }

    /* Compact logo + padding so the window-control cluster stays in-view on partial drawers */
    .lti-drawer .card-header .card-header-title .edverum-logo-compact.edverumLogo {
        width: 28px !important;
        height: 28px !important;
        margin-right: 0.5rem !important;
        flex-shrink: 0;
    }

    .lti-drawer .card-header .card-header-icon {
        padding-right: 0.25rem;
    }
}

/* Ensure dark theme applies when lti-drawer is rendered inside app-dark host */
.app-dark .lti-drawer .lti-drawer__footer {
    background-color: #0f1720 !important;
    border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
    color: #f8fafc !important;
}

/* Drawer chrome (header + body panel) — keeps hosted content like SCORM on-theme */
.app-dark .lti-drawer.p-drawer,
.app-dark .lti-drawer .card {
    background-color: var(--p-surface-900, #111827) !important;
    color: var(--p-text-color, #f9fafb);
}

.app-dark .lti-drawer .card-header,
.app-dark .lti-drawer .card-content {
    background-color: transparent !important;
    color: inherit;
}

.app-dark .lti-drawer .card-header .header-title {
    color: var(--p-text-color, #f9fafb);
}
</style>
